import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Globe, Wifi, Server, Gauge, Signal, Network } from 'lucide-react'
import SEO from '../../components/SEO'

const NetworkOperations = () => {
  const features = [
    {
      icon: Wifi,
      title: 'Secure WiFi Everywhere',
      description: 'Enterprise-grade wireless networks at all your properties with seamless roaming and top-tier security.'
    },
    {
      icon: Globe,
      title: 'Global Connectivity',
      description: 'Uninterrupted secure connectivity across homes, yachts, private jets, and vehicles worldwide.'
    },
    {
      icon: Server,
      title: 'Infrastructure Excellence',
      description: 'Premium network infrastructure designed for reliability, speed, and security.'
    },
    {
      icon: Gauge,
      title: 'Performance Optimization',
      description: 'Continuous monitoring and tuning to ensure maximum network performance at all times.'
    },
    {
      icon: Signal,
      title: 'Redundant Systems',
      description: 'Multiple backup connectivity options ensuring you\'re never without internet access.'
    },
    {
      icon: Network,
      title: 'Unified Management',
      description: 'Single pane of glass management across all your networks and locations.'
    }
  ]

  return (
    <>
      <SEO 
        title="Network Operations - Praetorian Works"
        description="Elite network operations for HNW and UHNW clients. Secure WiFi, global connectivity, and enterprise infrastructure across all your properties."
        keywords="private network management, luxury WiFi, secure connectivity HNW, enterprise networking for individuals"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent"></div>
          
          <div className="container-max relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl mb-8 shadow-2xl">
                <Globe className="w-10 h-10 text-secondary-900" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Network
                <span className="text-primary-500 ml-4">Operations</span>
              </h1>
              <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
                Seamless connectivity everywhere you go. Enterprise-class networks providing fast, 
                secure, and reliable internet at all your properties—from estates to yachts to aircraft.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20">
          <div className="container-max">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="bg-gradient-to-br from-secondary-800 to-black rounded-2xl p-8 border border-neutral-800 hover:border-primary-500/50 transition-all duration-300"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mb-6 shadow-lg">
                    <feature.icon className="w-8 h-8 text-secondary-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-black/30">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                Connected <span className="text-primary-500">Without Compromise</span>
              </h2>
              <p className="text-lg text-neutral-300 mb-6 leading-relaxed">
                Whether you're working from your home office, streaming entertainment on your yacht, or 
                conducting video calls from your private jet, our Network Operations ensure you have 
                the same fast, secure connection everywhere.
              </p>
              <p className="text-lg text-neutral-300 leading-relaxed">
                We design, implement, and manage enterprise-grade network infrastructure tailored to your 
                unique requirements, with redundant systems ensuring continuous connectivity and security 
                that protects your privacy at all times.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="container-max text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Stay Connected, Everywhere
              </h2>
              <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
                Learn how our Network Operations can provide seamless connectivity across all your properties.
              </p>
              <Link to="/contact" className="btn-primary text-lg">
                Schedule Consultation
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default NetworkOperations
